import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  inject,
} from '@angular/core';
import { translate } from '@jsverse/transloco';
import {
  Observable,
  Subject,
  Subscription,
  debounceTime,
  distinctUntilChanged,
  map,
  startWith,
  switchMap,
  tap,
} from 'rxjs';
import { KeyValuePair } from '@inmoment/the-kitchen';

import { AppSharedModule } from 'app/app-shared.module';
import { GroupService } from 'app/providers/semantria-api/group.service';
import {
  ItemLabelWrapper,
  ObjectLabel,
  ObjectLabelObjectType,
  ObjectLabelService,
} from 'app/providers/storage-api/object-label.service';
import { ItemLabel } from 'app/providers/storage-api/object-label.service';
import { AlertService } from 'app/services/alert.service';
import { ErrorObjectUtils } from 'app/utils/error-object.utils';

@Component({
  selector: 'app-edit-label-popout',
  imports: [AppSharedModule],
  templateUrl: './edit-label-popout.component.html',
  styleUrl: './edit-label-popout.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EditLabelPopoutComponent implements OnInit, OnDestroy {
  @Input() label?: ObjectLabel;
  @Input() objectType: ObjectLabelObjectType;
  @Input() objectLabels: ItemLabel[] = [];

  @Input() item: ItemLabelWrapper;

  @Output() labelTextUpdating = new EventEmitter<string>();
  @Output() labelUpdated = new EventEmitter<ObjectLabel>();
  @Output() labelApplied = new EventEmitter<ObjectLabel | Partial<ObjectLabel>>();
  @Output() labelRemoved = new EventEmitter<{ labelId: string; objectId: string }>();
  @Output() cancel = new EventEmitter();

  labelSuggestions: KeyValuePair[] = [];
  labels: ObjectLabel[] = [];
  selectedLabel: ObjectLabel;
  initialValue: string;
  searchValue: string;
  searchSubject = new Subject<string>();
  searchSubscription: Subscription;
  labelInputStatus = ErrorObjectUtils.create();

  groupId: string;

  isLoading;
  isProcessing;
  private shouldUpdate: boolean;

  private cdr = inject(ChangeDetectorRef);
  private labelService = inject(ObjectLabelService);
  private groupService = inject(GroupService);
  private alertService = inject(AlertService);

  ngOnInit(): void {
    this.groupId = this.groupService.getSelectedGroupId();
    this.searchValue = this.label?.label ?? '';
    this.initialValue = this.searchValue;
    //Insight labels should not be saved via label service
    this.shouldUpdate = this.item.type !== 'insight' && this.item.type !== 'insightFeed';

    const searchPipeline = (observable: Observable<any>) => {
      return observable.pipe(
        startWith(this.initialValue),
        tap((val) => {
          this.isLoading = true;
          this.labelTextUpdating.emit(val as string);
          this.validateLabel(val as string);
        }),
        debounceTime(300),
        distinctUntilChanged()
      );
    };

    const searchSubscriptionHandler = (res: any, filteredLabels: ObjectLabel[]) => {
      this.isLoading = false;
      this.labels = filteredLabels;
      this.labelSuggestions = filteredLabels
        .map((l) => ({ key: l.id, value: l.label }))
        .filter((kv) => !this.objectLabels.some((ol) => ol.id === kv.key));

      this.validateLabel(this.searchValue);

      if (this.label) {
        this.labels = [];
        this.labelSuggestions = [];
      }

      this.cdr.markForCheck();
    };

    const searchObservable$ =
      this.item.type === 'insight'
        ? searchPipeline(this.searchSubject).pipe(
            map((res) => ({
              res,
              filteredLabels: this.item.searchLabels.filter((l) => l.label.startsWith(res)),
            }))
          )
        : searchPipeline(this.searchSubject).pipe(
            switchMap((val) => this.labelService.getLabels(this.groupId, 0, 10, val, true)),
            map((res) => ({ res, filteredLabels: res.items }))
          );

    this.searchSubscription = searchObservable$.subscribe(({ res, filteredLabels }) => {
      searchSubscriptionHandler(res, filteredLabels);
    });
  }

  ngOnDestroy(): void {
    this.searchSubject.complete();
    if (this.searchSubscription) {
      this.searchSubscription.unsubscribe();
    }
  }

  private validateLabel(labelText: string) {
    this.labelInputStatus.state = 'success';
    this.labelInputStatus.text = '';

    if (labelText.length > 20) {
      this.labelInputStatus.state = 'error';
      this.labelInputStatus.text = translate('labels.edit-label.label-too-long', { maxLength: 20 });
      return;
    }

    if (
      this.objectLabels.some(
        (ol) => ol.label === labelText && (!this.label || ol.id !== this.label.id)
      )
    ) {
      this.labelInputStatus.state = 'error';
      this.labelInputStatus.text = translate('labels.edit-label.label-already-applied');
      return;
    }

    if (this.label && this.labels.some((l) => l.id !== this.label.id && l.label === labelText)) {
      this.labelInputStatus.state = 'error';
      this.labelInputStatus.text = translate('labels.edit-label.label-already-exists');
    }
    if (this.labels.length < 1 && this.item.type === 'insightFeed' && !this.isLoading) {
      this.labelInputStatus.state = 'error';
      this.labelInputStatus.text = translate('labels.edit-label.label-cannot-be-added-or-modified');
    }
  }

  onSuggestionSelected(suggestion: KeyValuePair) {
    this.searchValue = suggestion.value;
    this.labels = this.labels.filter((l) => l.id === suggestion.key);
    this.labelSuggestions = [suggestion];

    this.labelTextUpdating.emit(this.searchValue);

    this.validateLabel(this.searchValue);
  }

  updateOrApplyOrCreate() {
    if (this.isLoading || this.hasError) {
      return;
    }

    if (this.label) {
      this.update();
      return;
    }

    this.applyOrCreate();
  }

  async applyOrCreate() {
    if (this.isLoading || this.hasError) {
      return;
    }

    const existingLabel = this.labelSuggestions.find((ls) => ls.value === this.searchValue);
    let labelToApply = this.labels.find((l) => l.id === existingLabel?.key);
    if (!labelToApply) {
      if (!this.shouldUpdate) {
        this.labelApplied.emit({ groupId: this.groupId, label: this.searchValue });
        return;
      }
      labelToApply = await this.create();
    }

    this.apply(labelToApply);
  }

  private async update() {
    if (!this.hasChanges) {
      this.cancel.emit();
      return;
    }
    if (!this.shouldUpdate) {
      this.labelUpdated.emit(this.label);
      return;
    }
    this.isProcessing = true;
    try {
      const updatedLabel = await this.labelService.updateLabel(
        this.groupId,
        this.label.id,
        this.searchValue
      );
      this.alertService.showToast(
        translate('labels.edit-label.label-updated', {
          label: updatedLabel.label,
        })
      );
      this.labelUpdated.emit(updatedLabel);
    } finally {
      this.isProcessing = false;
      this.cdr.markForCheck();
    }
  }

  private async create() {
    this.isProcessing = true;
    try {
      return await this.labelService.createLabel(this.groupId, this.searchValue);
    } finally {
      this.isProcessing = false;
      this.cdr.markForCheck();
    }
  }

  private async apply(label: ObjectLabel) {
    if (!this.shouldUpdate) {
      this.labelApplied.emit(label);
      return;
    }
    this.isProcessing = true;
    try {
      await this.labelService.applyLabel(this.groupId, label.id, this.objectType, this.item.id);
      this.alertService.showToast(
        translate('labels.edit-label.label-applied', {
          label: label.label,
        })
      );
      this.labelApplied.emit(label);
    } finally {
      this.isProcessing = false;
      this.cdr.markForCheck();
    }
  }

  async remove() {
    if (!this.shouldUpdate) {
      this.labelRemoved.emit({ labelId: this.label.id, objectId: this.item.id });
      return;
    }
    this.isProcessing = true;
    try {
      await this.labelService.removeLabel(
        this.groupId,
        this.label.id,
        this.objectType,
        this.item.id
      );
      this.alertService.showToast(
        translate('labels.edit-label.label-removed', {
          label: this.label.label,
        })
      );
      this.labelRemoved.emit({ labelId: this.label.id, objectId: this.item.id });
    } finally {
      this.isProcessing = false;
      this.cdr.markForCheck();
    }
  }

  get hasError() {
    return !this.searchValue || this.labelInputStatus.state === 'error';
  }

  get hasChanges() {
    return this.searchValue !== this.initialValue;
  }
}
